import React, { useMemo } from "react";
import { DataTableLoading } from "./data-table-loading.js";
import { EmptyState } from "../../../components/ui/states/empty-state";
import { ErrorState } from "../../../components/ui/states/error-state";
import type { RowBase } from "../data-table.js";
import type { ColumnDef } from "../data-table.types.js";

type Variant = "empty" | "error" | "loading";

type Props<TRow extends RowBase> = {
  variant: Variant;
  message?: string;
  columns: ColumnDef<TRow>[];
  showActionsColumn?: boolean;
};

export const DataTableState = <TRow extends RowBase>({
  variant,
  columns,
  message,
  showActionsColumn = false,
}: Props<TRow>) => {
  const totalColumns = columns.length + (showActionsColumn ? 1 : 0);

  const config = useMemo(() => {
    const baseConfig = {
      empty: <EmptyState message="No data available" />,
      error: <ErrorState message="Error loading data. Please try again." />,
    } as Record<Variant, React.ReactElement>;

    const element = baseConfig[variant];
    return message && element
      ? React.cloneElement(element, { message } as Record<string, unknown>)
      : element;
  }, [variant, message]);

  if (variant === "loading") {
    return (
      <tbody className="border-gxe-border flex flex-col rounded-lg border bg-white px-4">
        <DataTableLoading
          columns={columns}
          showActionsColumn={showActionsColumn}
          loadingRowCount={5}
        />
      </tbody>
    );
  }

  return (
    <tbody className="border-gxe-border flex flex-col rounded-lg border bg-white px-4">
      <tr className="flex items-center justify-center">
        <td colSpan={totalColumns} className="px-4 py-12 text-center">
          {config}
        </td>
      </tr>
    </tbody>
  );
};
